import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Table, Tag } from 'antd';
import { loadAttemptRecords as loadAttemptRecordsAction } from 'actions/optimiserStatus';
import * as permissions from 'library/permissions';
import { recordTypeLabel } from './config';

export const PAGE_SIZE = 50;

const COLUMNS = [{ title: 'Record', dataIndex: 'summary', key: 'summary' }];

const renderDataSent = row => (
  <div className="bhp-optimiser-status__attempt-data">
    <strong>Data sent</strong>
    <pre>{JSON.stringify(row.body, null, 2)}</pre>
  </div>
);

class PublishAttemptDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: null, error: false, loading: false, page: 1
    };
    this.requestId = 0;
    this.load = this.load.bind(this);
    this.handleResend = this.handleResend.bind(this);
  }

  componentDidMount() {
    this.load(1);
  }

  componentDidUpdate(prevProps) {
    const { attemptId } = this.props;
    if (prevProps.attemptId !== attemptId) {
      // Drop the previous attempt at once so its header and Re-send never show for this one.
      this.load(1, { data: null });
    }
  }

  // Only the latest request may update the panel: a slow answer for an attempt or page
  // the user has already left is ignored.
  load(page, reset = {}) {
    const { attemptId, loadAttemptRecords } = this.props;
    this.requestId += 1;
    const { requestId } = this;
    this.setState({
      loading: true, error: false, page, ...reset
    });
    return loadAttemptRecords(attemptId, { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      .then((data) => {
        if (requestId === this.requestId) {
          this.setState({ data, loading: false });
        }
      })
      .catch(() => {
        if (requestId === this.requestId) {
          this.setState({ error: true, loading: false });
        }
      });
  }

  handleResend() {
    const { attemptId, onResend } = this.props;
    const { page } = this.state;
    return Promise.resolve(onResend(attemptId)).then(() => this.load(page));
  }

  renderHeader(attempt) {
    const canResend = attempt.can_retry && permissions.CanAdminOptimiser().hasPermissions();
    return (
      <div className="bhp-optimiser-status__attempt-header">
        <h3 className="bhp-optimiser-status__attempt-title">
          {`${recordTypeLabel(attempt.record_type)}: ${attempt.record_title}`}
        </h3>
        <div>
          {moment(attempt.created_date).format('DD MMM YYYY HH:mm')}
          {' '}
          <Tag color={attempt.succeeded ? 'green' : 'red'}>{attempt.succeeded ? 'Sent' : 'Failed'}</Tag>
        </div>
        {attempt.reason && <p className="bhp-optimiser-status__attempt-reason">{attempt.reason}</p>}
        {canResend && (
          <Button type="primary" size="small" className="bhp-optimiser-status__attempt-resend" onClick={this.handleResend}>
            Re-send
          </Button>
        )}
      </div>
    );
  }

  render() {
    const { onClose } = this.props;
    const {
      data, error, loading, page
    } = this.state;
    return (
      <div className="bhp-optimiser-status__attempt-detail">
        <Button size="small" icon="close" className="bhp-optimiser-status__attempt-close" onClick={onClose} />
        {error && <p>Could not load the details of this publish attempt.</p>}
        {!error && data && data.attempt && this.renderHeader(data.attempt)}
        {!error && (
          <Table
            rowKey="id"
            size="small"
            loading={loading}
            columns={COLUMNS}
            dataSource={data ? data.results : []}
            expandedRowRender={renderDataSent}
            locale={{ emptyText: data ? 'No record details were saved for this attempt.' : '' }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data ? data.count : 0,
              onChange: this.load,
              showTotal: total => `${total} ${total === 1 ? 'record' : 'records'}`
            }}
          />
        )}
      </div>
    );
  }
}

PublishAttemptDetail.propTypes = {
  attemptId: PropTypes.number.isRequired,
  loadAttemptRecords: PropTypes.func,
  onResend: PropTypes.func,
  onClose: PropTypes.func
};

PublishAttemptDetail.defaultProps = {
  loadAttemptRecords() {
    return Promise.resolve(null);
  },
  onResend() {
    return Promise.resolve();
  },
  onClose() {}
};

const mapDispatchToProps = dispatch => ({
  loadAttemptRecords(id, params) {
    return dispatch(loadAttemptRecordsAction(id, params));
  }
});

export { PublishAttemptDetail };
export default connect(null, mapDispatchToProps)(PublishAttemptDetail);
